import type {
  SupportFormAttachmentPolicy,
  SupportFormCategory,
  SupportFormSubcategory,
  SupportFormSubtopic,
} from '@/types'

export const SUPPORT_STATUSES = [
  'received',
  'queued',
  'in_progress',
  'waiting_on_customer',
  'resolved',
  'closed',
]

export const SUPPORT_SOURCES = ['form', 'email']

export const FILE_TYPE_OPTIONS = [
  { extension: 'png', label: 'PNG', mime: 'image/png' },
  { extension: 'jpg', label: 'JPG', mime: 'image/jpeg' },
  { extension: 'jpeg', label: 'JPEG', mime: 'image/jpeg' },
  { extension: 'pdf', label: 'PDF', mime: 'application/pdf' },
  { extension: 'txt', label: 'TXT', mime: 'text/plain' },
  { extension: 'json', label: 'JSON', mime: 'application/json' },
  { extension: 'log', label: 'LOG', mime: 'text/plain' },
]

export const DEFAULT_ATTACHMENT_EXTENSIONS = ['png', 'jpg', 'jpeg', 'pdf']

const KNOWN_MIME_TYPES = new Set(FILE_TYPE_OPTIONS.map((option) => option.mime))

const EXTENSION_TO_MIME = FILE_TYPE_OPTIONS.reduce<Record<string, string>>((acc, option) => {
  acc[option.extension] = option.mime
  return acc
}, {})

export const ensureArray = <T,>(value?: T[] | null): T[] => (Array.isArray(value) ? value : [])

export const dedupe = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean)))

export const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/(^-|-$)/g, '')

export const parsePositiveNumber = (value: string): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

export const parsePositiveInt = (value: string): number | null => {
  const parsed = Number(value)
  return Number.isFinite(parsed) && parsed > 0 ? Math.floor(parsed) : null
}

export const bytesToMbString = (value: number): string => {
  const mb = value / (1024 * 1024)
  const rounded = Math.round(mb * 100) / 100
  return Number.isFinite(rounded) ? String(rounded) : '5'
}

export const buildAllowedMimeTypes = (
  extensions: string[],
  existingMimeTypes: string[]
): string[] => {
  const selected = extensions
    .map((extension) => EXTENSION_TO_MIME[extension])
    .filter((mime): mime is string => Boolean(mime))
  const custom = existingMimeTypes.filter((mime) => !KNOWN_MIME_TYPES.has(mime))
  return dedupe([...selected, ...custom])
}

export const applyDefaultAttachmentTypes = (
  policy: SupportFormAttachmentPolicy
): SupportFormAttachmentPolicy => {
  if (!policy.enabled || policy.allowedExtensions.length > 0) {
    return policy
  }
  return {
    ...policy,
    allowedExtensions: [...DEFAULT_ATTACHMENT_EXTENSIONS],
    allowedMimeTypes: buildAllowedMimeTypes(
      DEFAULT_ATTACHMENT_EXTENSIONS,
      ensureArray(policy.allowedMimeTypes)
    ),
  }
}

export const normalizePolicy = (
  policy?: SupportFormAttachmentPolicy
): SupportFormAttachmentPolicy | undefined => {
  if (!policy) {
    return undefined
  }
  return applyDefaultAttachmentTypes({
    enabled: Boolean(policy.enabled),
    allowedExtensions: ensureArray(policy.allowedExtensions),
    allowedMimeTypes: ensureArray(policy.allowedMimeTypes),
    ...(policy.maxFiles ? { maxFiles: policy.maxFiles } : {}),
  })
}

export const normalizeSubtopicType = (
  value: string | undefined,
  count: number
): 'options' | 'open' | undefined => {
  if (value === 'options' || value === 'open') {
    return value
  }
  return count > 0 ? 'options' : undefined
}

export const normalizeSubtopic = (subtopic: SupportFormSubtopic): SupportFormSubtopic => ({
  ...subtopic,
  attachmentPolicy: normalizePolicy(subtopic.attachmentPolicy),
})

export const normalizeSubcategory = (
  subcategory: SupportFormSubcategory
): SupportFormSubcategory => {
  const rawSubtopics = ensureArray(subcategory.subtopics).map(normalizeSubtopic)
  const subtopicType = normalizeSubtopicType(subcategory.subtopicType, rawSubtopics.length)
  const subtopics = subtopicType === 'open' ? rawSubtopics.slice(0, 1) : rawSubtopics
  return {
    ...subcategory,
    subtopics,
    subtopicType,
    subtopicTitle: subcategory.subtopicTitle ?? '',
    attachmentPolicy: subtopicType ? undefined : normalizePolicy(subcategory.attachmentPolicy),
  }
}

export const normalizeCategory = (category: SupportFormCategory): SupportFormCategory => ({
  ...category,
  subcategories: ensureArray(category.subcategories).map(normalizeSubcategory),
})

export const createEmptySubtopic = (): SupportFormSubtopic => ({
  id: '',
  label: '',
})

export const createEmptySubcategory = (): SupportFormSubcategory => ({
  id: '',
  label: '',
  subtopics: [],
  subtopicType: undefined,
  subtopicTitle: '',
})

export const createEmptyCategory = (): SupportFormCategory => ({
  id: '',
  label: '',
  subcategories: [createEmptySubcategory()],
})

export const parseJson = (value?: string | null) => {
  if (!value) {
    return null
  }
  try {
    return JSON.parse(value)
  } catch {
    return null
  }
}

export const formatPayloadPreview = (payload: any) => {
  if (!payload || typeof payload !== 'object') {
    return { title: 'Unknown', subtitle: '' }
  }
  const innerPayload =
    payload.payload && typeof payload.payload === 'object' ? payload.payload : payload
  const email = innerPayload.email || innerPayload.from || ''
  const title = innerPayload.subject || innerPayload.name || email || 'Unknown'
  const message = innerPayload.message || innerPayload.body || ''
  return {
    title: title || 'Unknown',
    subtitle: message ? String(message).slice(0, 120) : '',
  }
}

export type TemplateDefaultsState = {
  maxMessageLength: string
  maxAttachmentSizeMb: string
  maxAttachments: string
}

export const DEFAULT_TEMPLATE_DEFAULTS: TemplateDefaultsState = {
  maxMessageLength: '500',
  maxAttachmentSizeMb: '5',
  maxAttachments: '3',
}
